import * as MenuItem from 'tradera/components/menu-item/menu-item';
import HoverMenu from './hover-menu/hover-menu';
import { getMenuGaAttributes } from './site-header.analytics.js';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { REDIRECT_TO_NEWDRAFT_QUERY } from 'tradera/apps/syi/constants';
import { myTraderaUrls } from 'tradera/apps/my-tradera/app/pages/routes';
import { Divider } from 'tradera/components/divider/divider';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import TemplatesIcon from '@tradera/blueprint/theme/icons2/templates.svg?jsx';
import ListIcon from '@tradera/blueprint/theme/icons2/list.svg?jsx';
import ShippingBagIcon from '@tradera/blueprint/theme/icons2/shipping-bag.svg?jsx';
import RatingPositiveIcon from '@tradera/blueprint/theme/icons2/rating-positive.svg?jsx';
import SoldIcon from '@tradera/blueprint/theme/icons2/sold.svg?jsx';
import UnsoldIcon from '@tradera/blueprint/theme/icons2/unsold.svg?jsx';
import SaleIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import VacationModeIcon from '@tradera/blueprint/theme/icons2/vacation.svg?jsx';
import { SCROLL_ANCHORS } from 'tradera/apps/my-tradera/app/components/settings/settings-constants';

const getGaAttributes = (label: string) =>
    getMenuGaAttributes('Top navigation', 'Selling menu', label);

type SellMenuProps = {
    showMenu: boolean;
};

export const SellMenu = ({ showMenu }: SellMenuProps) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const isTraderaShopEnabled = useIsFeatureEnabled('tradera-shop');
    const vacationModeFeatureEnabled = useIsFeatureEnabled('vacation-mode');

    return (
        <HoverMenu showMenu={showMenu}>
            <MenuItem.Wrapper>
                <MenuItem.Button
                    href={toLocalizedUrl(
                        `/selling?${REDIRECT_TO_NEWDRAFT_QUERY}`,
                    )}
                    className="px-2"
                    icon={<PenIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Create new listing')}>
                    {t('siteHeader_createItem')}
                </MenuItem.Button>
                <MenuItem.Button
                    href={toLocalizedUrl('/selling/templates')}
                    className="px-2"
                    icon={<TemplatesIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Listing templates')}>
                    {t('siteHeader_templates')}
                </MenuItem.Button>
                <MenuItem.Button
                    href={toLocalizedUrl(myTraderaUrls.listings)}
                    className="px-2"
                    icon={<SaleIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Active listings')}>
                    {t('siteHeader_activeItems')}
                </MenuItem.Button>
                <MenuItem.Button
                    href={toLocalizedUrl('/my/sold')}
                    className="px-2"
                    icon={<SoldIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Sold listings')}>
                    {t('siteHeader_soldItems')}
                </MenuItem.Button>
                <MenuItem.Button
                    href={toLocalizedUrl(myTraderaUrls.listingsUnsold)}
                    className="px-2"
                    icon={<UnsoldIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Unsold listings')}>
                    {t('siteHeader_unsoldItems')}
                </MenuItem.Button>
                {vacationModeFeatureEnabled ? (
                    <MenuItem.Button
                        href={toLocalizedUrl(
                            `/my/settings/selling${SCROLL_ANCHORS.VACATION_MODE}`,
                        )}
                        className="px-2"
                        icon={
                            <VacationModeIcon className="icon icon-md mr-1" />
                        }
                        brandColorOnHover
                        {...getGaAttributes('Vacation mode')}>
                        {t('siteHeader_vacationMode')}
                    </MenuItem.Button>
                ) : (
                    <MenuItem.Button
                        href={toLocalizedUrl('/my/picklist')}
                        className="px-2"
                        icon={<ListIcon className="icon icon-md mr-1" />}
                        brandColorOnHover
                        {...getGaAttributes('Picklist')}>
                        {t('siteHeader_picklist')}
                    </MenuItem.Button>
                )}
                <Divider thick className="my-0" />
                {isTraderaShopEnabled && (
                    <MenuItem.Button
                        href={toLocalizedUrl('/tradera-shop')}
                        className="px-2"
                        icon={<ShippingBagIcon className="icon icon-md mr-1" />}
                        brandColorOnHover
                        {...getGaAttributes('To Tradera Shop')}>
                        {t('siteHeader_buyShippingSupplies')}
                    </MenuItem.Button>
                )}
                <MenuItem.Button
                    href={toLocalizedUrl('/my/leave-feedback/seller')}
                    className="px-2"
                    border={false}
                    icon={<RatingPositiveIcon className="icon icon-md mr-1" />}
                    brandColorOnHover
                    {...getGaAttributes('Rate buyer')}>
                    {t('siteHeader_rateBuyer')}
                </MenuItem.Button>
            </MenuItem.Wrapper>
        </HoverMenu>
    );
};
